// The damage formula and poise (decision 0051: "attack × ability multiplier against defence; damage types as tags
// with resistances; critical hits; poise and stagger"). Pure functions; the crit roll comes from the game's seeded
// stream, never Math.random, so a fight replays exactly.

/** What one blow's damage depends on. */
export interface DamageInput {
  /** The attacker's attack (its current value). */
  attack: number;
  /** The move's multiplier. */
  power: number;
  /** The target's defence. */
  defence: number;
  /** The defence curve's constant: damage × K / (K + defence) (defence K halves it). */
  defenceK: number;
  /** The attacker's crit chance (0..1) and multiplier. */
  critChance: number;
  critMult: number;
  /** A roll in [0, 1) from the game's seeded stream: a crit when it is below the chance. */
  roll: number;
  /** The target's resistance to the blow's damage types, 0..1 (1 is immune). Default 0. */
  resist?: number;
  /** Any further multiplier (difficulty, a counter, an assist's damage-taken slider). Default 1. */
  scale?: number;
}

/** The result: whole points (at least 1 when anything gets through), and whether it crit. */
export interface DamageResult {
  amount: number;
  crit: boolean;
  /** Before rounding, for tests and logs. */
  raw: number;
}

/** attack × power × K / (K + defence) × (1 − resist) × scale, × critMult on a crit; rounded to whole points, at least
 *  1 when the raw value is above 0. */
export function damage(d: DamageInput): DamageResult {
  const crit = d.roll < d.critChance;
  const mitigation = d.defenceK / (d.defenceK + Math.max(0, d.defence));
  const raw = Math.max(0, d.attack * d.power * mitigation * (1 - Math.min(1, Math.max(0, d.resist ?? 0))) * (d.scale ?? 1) * (crit ? d.critMult : 1));
  return { amount: raw > 0 ? Math.max(1, Math.round(raw)) : 0, crit, raw };
}

/** Poise after a blow: `poise − hit`; broken (a stagger) when it reaches 0 outside armour, then refilled to `max`. */
export function poiseAfter(poise: number, hit: number, max: number, armoured: boolean): { poise: number; broken: boolean } {
  const left = poise - Math.max(0, hit);
  if (armoured) return { poise: Math.max(0, left), broken: false };
  if (left <= 0) return { poise: max, broken: true };
  return { poise: left, broken: false };
}
